<template>
  <Teleport to="body">
    <Transition name="gloss-pop">
      <div
        v-if="visible && entry"
        ref="popupEl"
        class="gloss-popup"
        :style="positionStyle"
        role="tooltip"
        @click.stop
        @mouseenter="$emit('hover-in')"
        @mouseleave="$emit('hover-out')"
      >
        <div class="gloss-popup-header">
          <span class="gloss-popup-term">{{ displayTerm }}</span>
          <span v-if="entry.category" class="gloss-popup-cat">{{ entry.category }}</span>
          <button class="gloss-popup-close" @click="$emit('close')" aria-label="Tutup">✕</button>
        </div>
        <div class="gloss-popup-body">
          <p class="gloss-popup-def">{{ entry.definition }}</p>
          <div v-if="entry.example" class="gloss-popup-example">
            <span class="gloss-popup-example-label">Contoh</span>
            <p class="gloss-popup-example-text">{{ entry.example }}</p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

const props = defineProps({
  term:    { type: String,  default: null },
  entry:   { type: Object,  default: null },
  visible: { type: Boolean, default: false },
  anchorRect: { type: Object, default: null }, // DOMRect of clicked span
})
const popupEl = ref(null)
const positionStyle = ref({})

const displayTerm = computed(() => props.term || '')

async function reposition() {
  if (!props.anchorRect || !popupEl.value) return
  await nextTick()

  const popup = popupEl.value
  const pw = popup.offsetWidth || 300
  const ph = popup.offsetHeight || 180
  const { top, left, bottom, right } = props.anchorRect
  const vw = window.innerWidth
  const vh = window.innerHeight
  const GAP = 10

  // Prefer below; fallback to above
  let y = bottom + GAP
  if (y + ph > vh - GAP) y = top - ph - GAP

  // Prefer left-aligned to anchor; clamp to viewport
  let x = left
  if (x + pw > vw - GAP) x = vw - pw - GAP
  if (x < GAP) x = GAP

  positionStyle.value = { left: `${Math.round(x)}px`, top: `${Math.round(y)}px` }
}

watch(() => [props.visible, props.anchorRect], ([v]) => { if (v) nextTick(reposition) })

const emit = defineEmits(['close', 'hover-in', 'hover-out'])

function onKeydown(e) {
  if (e.key === 'Escape' && props.visible) {
    e.stopPropagation()
    emit('close')
  }
}

function onOutsideClick(e) {
  if (props.visible && popupEl.value && !popupEl.value.contains(e.target)) {
    emit('close')
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
  document.addEventListener('click', onOutsideClick, true)
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  document.removeEventListener('click', onOutsideClick, true)
})
</script>

<style scoped>
.gloss-popup {
  position: fixed;
  z-index: 9999;
  width: 300px;
  max-width: calc(100vw - 24px);
  background:
    linear-gradient(
      135deg,
      color-mix(in srgb, var(--accent-blue, #3b82f6) 6%, var(--bg-card, #111827)),
      var(--bg-card, #111827)
    );
  border: 1px solid color-mix(in srgb, var(--accent-blue, #3b82f6) 35%, var(--border-soft, #1e2a3e));
  border-radius: 12px;
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(255, 255, 255, 0.04) inset,
    0 0 32px color-mix(in srgb, var(--accent-blue, #3b82f6) 10%, transparent);
  overflow: hidden;
  pointer-events: all;
}

.gloss-popup-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 8px;
  border-bottom: 1px solid var(--border-soft, rgba(255,255,255,0.07));
  background: rgba(255, 255, 255, 0.025);
}

.gloss-popup-term {
  font-weight: 700;
  font-size: 13px;
  color: var(--accent-blue-light, #7db4f5);
  text-decoration: underline;
  text-decoration-style: dashed;
  text-underline-offset: 3px;
  flex: 1;
  min-width: 0;
  letter-spacing: 0.2px;
}

.gloss-popup-cat {
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--text-dim, #6b7a8d);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--border-soft, rgba(255,255,255,0.08));
  border-radius: 6px;
  padding: 2px 7px;
  white-space: nowrap;
  flex-shrink: 0;
}

.gloss-popup-close {
  background: transparent;
  border: none;
  color: var(--text-dim, #6b7a8d);
  cursor: pointer;
  font-size: 11px;
  padding: 2px 5px;
  border-radius: 4px;
  line-height: 1;
  transition: color 0.12s, background 0.12s;
  flex-shrink: 0;
}
.gloss-popup-close:hover {
  color: var(--text-base, #e2e8f0);
  background: rgba(255, 255, 255, 0.07);
}

.gloss-popup-body {
  padding: 10px 12px 12px;
}

.gloss-popup-def {
  margin: 0 0 8px;
  font-size: 12.5px;
  line-height: 1.55;
  color: var(--text-base, #e2e8f0);
}

.gloss-popup-example {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--border-soft, rgba(255,255,255,0.07));
  border-left: 3px solid color-mix(in srgb, var(--accent-purple, #8b5cf6) 60%, transparent);
  border-radius: 0 6px 6px 0;
  padding: 7px 10px;
}

.gloss-popup-example-label {
  display: block;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: var(--accent-purple, #8b5cf6);
  margin-bottom: 4px;
}

.gloss-popup-example-text {
  margin: 0;
  font-size: 11.5px;
  line-height: 1.5;
  color: var(--text-muted, #94a3b8);
}

/* ── Transition ─────────────────────────────────────────────────────────────── */
.gloss-pop-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.gloss-pop-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.gloss-pop-enter-from  { opacity: 0; transform: translateY(-4px) scale(0.97); }
.gloss-pop-leave-to    { opacity: 0; transform: translateY(-4px) scale(0.97); }
</style>
